export class ApiError extends Error {
  status: number
  details?: unknown
  constructor(message: string, status: number, details?: unknown) {
    super(message)
    this.status = status
    this.details = details
  }
}

export const standaloneMode = import.meta.env.VITE_STANDALONE === 'true'

/**
 * The public local-creator workspace deliberately uses the same browser-only
 * adapter as the downloadable HTML. It never sends creation data to the API.
 */
export function isOnlineLocalCreatorMode() {
  return !standaloneMode && typeof window !== 'undefined' && /^\/local-create(?:\/|$)/.test(window.location.pathname)
}

export function isLocalCreatorMode() {
  return standaloneMode || isOnlineLocalCreatorMode()
}

function cookie(name: string) {
  return document.cookie.split('; ').find((part) => part.startsWith(`${name}=`))?.slice(name.length + 1)
}

export async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  // An online local workspace has no platform account. Keeping auth empty
  // prevents a browser-local creator identity from leaking into public pages.
  if (isOnlineLocalCreatorMode() && path === '/api/auth/me') return { user: null } as T
  if (isLocalCreatorMode()) {
    const { standaloneApi } = await import('./standalone-api')
    return standaloneApi<T>(path, options)
  }
  const method = (options.method || 'GET').toUpperCase()
  const csrf = !['GET', 'HEAD', 'OPTIONS'].includes(method) ? cookie('lumi_csrf') : undefined
  const hasBody = options.body !== undefined && options.body !== null
  const response = await fetch(path, {
    credentials: 'include',
    ...options,
    headers: options.body instanceof FormData
      ? { ...(csrf ? { 'x-csrf-token': csrf } : {}), ...options.headers }
      : { ...(hasBody ? { 'Content-Type': 'application/json' } : {}), ...(csrf ? { 'x-csrf-token': csrf } : {}), ...options.headers },
  })
  const contentType = response.headers.get('content-type') || ''
  const data = contentType.includes('application/json') ? await response.json() : await response.text()
  if (!response.ok) throw new ApiError(typeof data === 'string' ? data : data.error || '请求失败', response.status, data)
  return data as T
}

export const postJson = <T>(path: string, body: unknown) => api<T>(path, { method: 'POST', body: JSON.stringify(body) })
export const putJson = <T>(path: string, body: unknown) => api<T>(path, { method: 'PUT', body: JSON.stringify(body) })
export const patchJson = <T>(path: string, body: unknown) => api<T>(path, { method: 'PATCH', body: JSON.stringify(body) })
export const deleteJson = <T>(path: string, body?: unknown) => api<T>(path, { method: 'DELETE', ...(body === undefined ? {} : { body: JSON.stringify(body) }) })

export async function downloadCsv(path: string, filename: string) {
  let csv: string
  if (isLocalCreatorMode()) csv = await api<string>(path)
  else {
    const response = await fetch(path, { credentials: 'include' })
    if (!response.ok) throw new ApiError('导出失败', response.status)
    csv = await response.text()
  }
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

export async function downloadJson(path: string, filename: string) {
  const data = await api<unknown>(path)
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

export async function downloadFile(path: string, filename: string) {
  if (isLocalCreatorMode()) throw new ApiError('本地创作模式不支持服务器文件导出；请导出项目 JSON 或 CSV 备份。', 400)
  const response = await fetch(path, { credentials: 'include' })
  if (!response.ok) {
    const contentType = response.headers.get('content-type') || ''
    const data = contentType.includes('application/json') ? await response.json() : await response.text()
    throw new ApiError(typeof data === 'string' ? data : data.error || '导出失败', response.status, data)
  }
  const blob = await response.blob()
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}
